import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, MapPin, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
interface County {
  name: string;
  cities: string[];
}
const counties: County[] = [{
  name: 'Hudson County',
  cities: ['Union City', 'Jersey City', 'North Bergen', 'West New York']
}, {
  name: 'Bergen County',
  cities: ['Hackensack', 'Englewood', 'Garfield', 'Lodi']
}, {
  name: 'Passaic County',
  cities: ['Paterson', 'Passaic', 'Clifton']
}, {
  name: 'Essex County',
  cities: ['Newark', 'Irvington', 'East Orange']
}, {
  name: 'Union County',
  cities: ['Elizabeth', 'Union', 'Linden']
}];
export function UrgencySection() {
  const {
    t
  } = useLanguage();
  const [openCounty, setOpenCounty] = useState<number | null>(null);
  const toggleCounty = (index: number) => {
    setOpenCounty(openCounty === index ? null : index);
  };
  return <section className="bg-brand-primary py-16 md:py-20 text-white relative overflow-hidden" data-id="element-187">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10" style={{
      backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
      backgroundSize: '24px 24px'
    }} data-id="element-188"></div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-189">
        <motion.div className="text-center" initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} data-id="element-190">
          <div className="inline-flex items-center justify-center space-x-3 bg-red-500/20 border border-red-400/50 text-white py-2 px-6 rounded-full mb-8" data-id="element-191">
            <AlertTriangle className="w-6 h-6 text-brand-accent" data-id="element-192" />
            <h2 className="text-xl md:text-2xl font-bold uppercase tracking-wide" data-id="element-193">
              {t('urgency.badge')}
            </h2>
          </div>

          <p className="text-xl md:text-2xl font-medium mb-10 leading-relaxed" data-id="element-194">
            {t('urgency.body1')}{' '}
            <span className="font-bold text-brand-accent" data-id="element-195">
              {t('urgency.body2')}
            </span>{' '}
            {t('urgency.body3')}
          </p>

          {/* Coverage Zones */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 mb-10 border border-white/20" data-id="element-196">
            <div className="flex items-center justify-center mb-2 text-brand-accent" data-id="element-197">
              <MapPin className="w-5 h-5 mr-2" data-id="element-198" />
              <h3 className="text-lg font-semibold uppercase tracking-wider" data-id="element-199">
                {t('urgency.coverageTitle')}
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-6" data-id="element-200">
              {counties.map((county, index) => <div key={index} className="bg-white/10 rounded-xl border border-white/15 overflow-hidden" data-id="element-201">
                  <button onClick={() => toggleCounty(index)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/10 transition-colors" data-id="element-202">
                    <span className="font-bold text-sm md:text-base" data-id="element-203">
                      {county.name}
                    </span>
                    <ChevronDown className={`w-4 h-4 text-brand-accent flex-shrink-0 transition-transform duration-300 ${openCounty === index ? 'rotate-180' : ''}`} data-id="element-204" />
                  </button>
                  <AnimatePresence data-id="element-205">
                    {openCounty === index && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} transition={{
                  duration: 0.25
                }} data-id="element-206">
                        <ul className="px-4 pb-3 space-y-1.5" data-id="element-207">
                          {county.cities.map((city, cityIndex) => <li key={cityIndex} className="flex items-center space-x-2 text-sm text-gray-200" data-id="element-208">
                              <span className="w-1.5 h-1.5 rounded-full bg-brand-accent flex-shrink-0" data-id="element-209"></span>
                              <span data-id="element-210">{city}</span>
                            </li>)}
                        </ul>
                      </motion.div>}
                  </AnimatePresence>
                </div>)}
            </div>
          </div>

          <p className="text-lg text-gray-200 mb-10 italic" data-id="element-211">
            {t('urgency.warning')}
          </p>

          <a href="https://wa.me/15514017684?text=Hola,%20quiero%20reservar%20mi%20cupo%20antes%20de%20que%20se%20llene%20mi%20zona" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-full sm:w-auto bg-brand-accent text-brand-navy font-extrabold text-lg md:text-xl py-5 px-8 rounded-lg shadow-[0_0_30px_rgba(251,191,36,0.4)] hover:bg-yellow-300 hover:scale-105 transition-all duration-300 min-h-[60px]" data-id="element-212">
            {t('urgency.cta')}
          </a>
        </motion.div>
      </div>
    </section>;
}